import { Component, HostListener, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpService } from 'src/app/services/http-service';
import { HomeComponent } from 'src/app/home/home.component';
import { loadStripe } from '@stripe/stripe-js';
import { SubscriptionTypeEnum } from 'src/app/models/user/user-model';
import { environment } from 'src/environments/environment';
import { SUBSCRIPTIONS } from 'src/app/helpers/constants';
import { DOCUMENT } from '@angular/common';
import { PosthogService } from 'src/app/services/posthog.service';

export interface SubscriptionsModalData {
  homeComponent: HomeComponent;
  subscriptionType: SubscriptionTypeEnum;
}

export interface SubscriptionsModalResult {}

@Component({
  selector: 'subscriptions-modal',
  templateUrl: 'subscriptions-modal.html',
})
export class SubscriptionsModal implements OnInit, OnDestroy {
  subscriptionTypes = SubscriptionTypeEnum;
  itemsDirection: string = (typeof window !== 'undefined' && window.innerWidth < 769) ? 'column' : 'row';
  private modalOpenTime: number;
  private clickedPlan: string = null;
  private checkoutInitiated: boolean = false;
  
  // Quick Wins: Pattern usage tracking
  userPatternCount: number = 0;
  remainingFreePatterns: number = 0;
  showUrgencyBanner: boolean = false;
  usagePercentage: number = 0;
  isFreeUser: boolean = false;

  constructor(
    public httpService: HttpService,
    @Inject(DOCUMENT) private document: Document,
    public dialogRef: MatDialogRef<SubscriptionsModal>,
    @Inject(MAT_DIALOG_DATA) public data: SubscriptionsModalData,
    private posthog: PosthogService
  ) {}

  async ngOnInit() {
    this.modalOpenTime = Date.now();

    // Quick Wins: Calculate pattern usage
    // Only show usage/urgency for users without active subscriptions
    const hasActiveSubscription = this.httpService.isSubscriptionActive();
    this.isFreeUser = !hasActiveSubscription;

    if (this.isFreeUser) {
      this.userPatternCount = await this.getUserPatternCount();
      this.remainingFreePatterns = Math.max(0, 3 - this.userPatternCount);
      this.usagePercentage = Math.min(100, (this.userPatternCount / 3) * 100);
      this.showUrgencyBanner = this.remainingFreePatterns <= 1;
    } else {
      // User has active subscription - no need to show usage tracking
      this.userPatternCount = 0;
      this.remainingFreePatterns = 0;
      this.usagePercentage = 0;
      this.showUrgencyBanner = false;
    }

    // PostHog: Track detailed modal open with urgency context
    this.posthog.captureEvent('pricing_modal_opened', {
      user_subscription_type: this.data.subscriptionType,
      has_patterns: this.userPatternCount > 0,
      pattern_count: this.userPatternCount,
      remaining_free_patterns: this.remainingFreePatterns,
      usage_percentage: this.usagePercentage,
      show_urgency_banner: this.showUrgencyBanner,
      device_type: this.getDeviceType(),
      has_active_subscription: hasActiveSubscription,
      // Pricing context for revenue optimization
      monthly_price: 5.0,
      yearly_price: 50.0,
      yearly_savings_percent: 17, // (5*12 - 50) / (5*12) * 100
    });
    
    // Track urgency banner shown
    if (this.showUrgencyBanner) {
      this.posthog.captureEvent('pricing_urgency_shown', {
        remaining_patterns: this.remainingFreePatterns,
        pattern_count: this.userPatternCount,
      });
    }
    
    this.resize();
  }

  ngOnDestroy() {
    // Track modal close if no checkout was initiated
    if (!this.checkoutInitiated) {
      const timeOnModal = Math.round((Date.now() - this.modalOpenTime) / 1000);
      
      this.posthog.captureEvent('pricing_modal_closed_without_action', {
        time_on_modal_seconds: timeOnModal,
        clicked_plan: this.clickedPlan,
        viewed_plans: this.getAvailablePlans(),
        dismissal_type: 'manual_close'
      });
    }
  }

  private async getUserPatternCount(): Promise<number> {
    try {
      const patterns = await this.httpService.getPatterns(0, '');
      return patterns.length;
    } catch {
      return 0;
    }
  }

  private getDeviceType(): string {
    if (window.innerWidth < 769) return 'mobile';
    if (window.innerWidth < 1024) return 'tablet_portrait';
    if (window.innerWidth < 1366) return 'tablet_landscape';
    return 'desktop';
  }

  private getAvailablePlans(): string[] {
    return ['monthly', 'yearly', 'free'];
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.resize();
  }

  private resize() {
    console.log('resize', window.innerWidth);
    // Mobile: column layout (< 769px)
    // Tablet and Desktop: row layout (>= 769px)
    if (window.innerWidth < 769) {
      this.itemsDirection = 'column';
    } else {
      this.itemsDirection = 'row';
    }
  }

  onClose(): void {
    // Track explicit close button click
    const timeOnModal = Math.round((Date.now() - this.modalOpenTime) / 1000);
    
    this.posthog.captureEvent('pricing_modal_close_button_clicked', {
      time_on_modal_seconds: timeOnModal,
      clicked_plan: this.clickedPlan,
    });
    
    this.dialogRef.close();
  }

  async onClickSubscription(subscriptionType: SubscriptionTypeEnum) {
    const planName = this.getPlanName(subscriptionType);
    this.clickedPlan = planName;
    
    // PostHog: Track plan click
    this.posthog.captureEvent('pricing_plan_clicked', {
      plan_name: planName,
      plan_price: this.getPlanPrice(subscriptionType),
      plan_currency: 'USD',
      current_subscription: this.data.subscriptionType,
      time_before_click_seconds: Math.round((Date.now() - this.modalOpenTime) / 1000),
      pattern_count: await this.getUserPatternCount(),
    });

    // Skip checkout for free plan
    if (subscriptionType === SubscriptionTypeEnum.None) {
      this.dialogRef.close();
      return;
    }

    try {
      this.checkoutInitiated = true;
      
      // PostHog: Track checkout initiation
      this.posthog.captureEvent('checkout_initiated', {
        plan_name: planName,
        plan_price: this.getPlanPrice(subscriptionType),
        currency: 'USD',
      });

      const stripe = await loadStripe(environment.stripeKey);
      const data = await this.httpService.createCheckoutSession(subscriptionType);
      const subscription = SUBSCRIPTIONS[subscriptionType - 1];
      const instance = this;

      // PostHog: Track Stripe redirect
      instance.posthog.captureEvent('stripe_checkout_opened', {
        plan_name: planName,
        session_id: data.sessionId,
      });
      
      // Call Stripe.js method to redirect to the new Checkout page
      stripe
        .redirectToCheckout({ sessionId: data.sessionId })
        .then(instance.handleResult);
    } catch (error) {
      // PostHog: Track checkout error
      this.posthog.captureEvent('checkout_failed', {
        plan_name: planName,
        error_message: error.message || 'Unknown error',
      });
      
      console.error('Checkout error:', error);
    }
  }

  private getPlanName(subscriptionType: SubscriptionTypeEnum): string {
    switch (subscriptionType) {
      case SubscriptionTypeEnum.Monthly:
        return 'monthly';
      case SubscriptionTypeEnum.Yearly:
        return 'yearly';
      case SubscriptionTypeEnum.None:
        return 'free';
      default:
        return 'unknown';
    }
  }

  private getPlanPrice(subscriptionType: SubscriptionTypeEnum): number {
    switch (subscriptionType) {
      case SubscriptionTypeEnum.Monthly:
        return 5.0;
      case SubscriptionTypeEnum.Yearly:
        return 50.0;
      case SubscriptionTypeEnum.None:
        return 0.0;
      default:
        return 0.0;
    }
  }

  handleResult(handleResult: any) {
    console.log('handleResult', handleResult);
  }
}
